import { useCallback, useRef, useState } from 'react';
import { useMedia, useInvalidate, type Media } from '../../lib/queries.js';
import { uploadFile, ACCEPTED_TYPES, MAX_FILE_BYTES } from '../../lib/upload.js';
import { errorMessage } from '../../lib/api.js';
import { Button, EmptyState, Loading, Modal, Input, useToast, cx, Spinner,
  IconImage, IconDocument,
} from '../../components/ui/index.js';
import { bytes, date } from '../../lib/format.js';

export function useUploader(blogId?: string) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const [uploading, setUploading] = useState<{ name: string; pct: number }[]>([]);

  const upload = useCallback(async (files: FileList | File[]): Promise<Media[]> => {
    const list = Array.from(files);
    const results: Media[] = [];

    for (const file of list) {
      if (file.size > MAX_FILE_BYTES) {
        toast(`"${file.name}" ใหญ่เกิน 10MB — ข้ามไฟล์นี้`, 'error');
        continue;
      }
      setUploading((u) => [...u, { name: file.name, pct: 0 }]);
      try {
        const { media, duplicate } = await uploadFile(file, {
          blogId,
          onProgress: (pct) => setUploading((u) => u.map((x) => (x.name === file.name ? { ...x, pct } : x))),
        });
        results.push(media);
        if (duplicate) toast(`"${file.name}" มีอยู่ในคลังแล้ว — ใช้ไฟล์เดิม`, 'info');
      } catch (err) {
        toast(`อัปโหลด "${file.name}" ไม่สำเร็จ: ${errorMessage(err)}`, 'error');
      } finally {
        setUploading((u) => u.filter((x) => x.name !== file.name));
      }
    }

    if (results.length) {
      invalidate(['media'], ['quota']);
      toast(`อัปโหลด ${results.length} ไฟล์เรียบร้อย`);
    }
    return results;
  }, [blogId, invalidate, toast]);

  return { upload, uploading };
}

export function DropZone({
  onFiles, uploading, compact,
}: { onFiles: (files: FileList | File[]) => void; uploading: { name: string; pct: number }[]; compact?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
      }}
      className={cx(
        'rounded-xl border-2 border-dashed text-center transition-colors',
        compact ? 'p-5' : 'p-10',
        over ? 'border-[var(--brand)] bg-[var(--brand-soft)]' : 'border-[var(--line)]',
      )}
    >
      <input
        ref={inputRef} type="file" multiple accept={ACCEPTED_TYPES} className="hidden"
        onChange={(e) => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }}
      />
      <p className="text-sm text-[var(--ink-2)]">
        ลากไฟล์มาวางที่นี่ หรือ{' '}
        <button type="button" onClick={() => inputRef.current?.click()}
                className="font-medium text-[var(--brand)] hover:underline">เลือกไฟล์</button>
      </p>
      <p className="mt-1 text-xs text-[var(--ink-3)]">
        JPEG · PNG · WebP · GIF · AVIF · PDF — ไม่เกิน 10MB ต่อไฟล์
        <br />รูปภาพจะถูกย่อและแปลงเป็น WebP ในเครื่องคุณก่อนอัปโหลด เพื่อให้เว็บโหลดเร็วขึ้น
      </p>

      {uploading.length > 0 && (
        <ul className="mx-auto mt-4 max-w-sm space-y-2 text-left">
          {uploading.map((u) => (
            <li key={u.name}>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-[var(--ink-2)]">{u.name}</span>
                <span className="tabular-nums text-[var(--ink-3)]">{u.pct}%</span>
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-[var(--sunken)]">
                <div className="h-full bg-[var(--brand)] transition-all" style={{ width: `${u.pct}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function MediaGrid({
  items, selectedId, onSelect,
}: { items: Media[]; selectedId?: string | null; onSelect: (m: Media) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {items.map((m) => {
        const isImage = m.mimeType.startsWith('image/');
        return (
          <button
            key={m.id} type="button" onClick={() => onSelect(m)}
            className={cx(
              'group overflow-hidden rounded-xl border text-left transition-all',
              selectedId === m.id
                ? 'border-[var(--brand)] ring-2 ring-brand-500/30'
                : 'border-[var(--line)] hover:border-[var(--line)]',
            )}
          >
            <div className="grid aspect-square place-items-center bg-[var(--sunken)]">
              {isImage ? (
                <img src={m.variants?.thumb ?? m.publicUrl} alt={m.altText ?? ''}
                     loading="lazy" decoding="async"
                     className="h-full w-full object-cover transition-transform group-hover:scale-105" />
              ) : (
                <IconDocument size={26} className="text-[var(--ink-3)]" />
              )}
            </div>
            <div className="px-2.5 py-2">
              <p className="truncate text-xs font-medium text-[var(--ink-2)]">{m.originalName}</p>
              <p className="text-[11px] text-[var(--ink-3)]">
                {bytes(m.sizeBytes)}{m.width ? ` · ${m.width}×${m.height}` : ''}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function MediaPicker({
  open, onClose, onSelect, blogId,
}: { open: boolean; onClose: () => void; onSelect: (m: Media) => void; blogId?: string }) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const media = useMedia({ page, perPage: 24, type: 'image', q });
  const { upload, uploading } = useUploader(blogId);

  return (
    <Modal open={open} onClose={onClose} title="เลือกรูปจากคลังไฟล์" size="xl">
      <div className="space-y-4">
        <DropZone compact uploading={uploading}
                  onFiles={async (files) => {
                    const uploaded = await upload(files);
                    if (uploaded[0]) onSelect(uploaded[0]);
                  }} />

        <Input placeholder="ค้นหาจากชื่อไฟล์…" value={q}
               onChange={(e) => { setQ(e.target.value); setPage(1); }} />

        {media.isLoading ? <Loading label="" /> : !media.data?.data.length ? (
          <EmptyState icon={<IconImage size={22} />} title="ยังไม่มีรูปในคลัง"
                      description="อัปโหลดรูปแรกด้วยการลากไฟล์มาวางด้านบน" />
        ) : (
          <>
            <MediaGrid items={media.data.data} onSelect={onSelect} />
            {media.data.meta.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>ก่อนหน้า</Button>
                <span className="text-sm text-[var(--ink-3)]">{page} / {media.data.meta.totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= media.data.meta.totalPages}
                        onClick={() => setPage((p) => p + 1)}>ถัดไป</Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
